import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowDown, ArrowUpRight } from 'lucide-react';
import { IMAGES } from '../data/content';
import { scrollToId } from '../lib/scroll';

const LINES = [
  { text: 'Built around' },
  { text: 'your life,', italic: true },
  { text: 'built to last.' },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '22%']);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.16]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative flex min-h-screen items-end overflow-hidden bg-espresso">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.hero} alt="Luxury modern infill home at dusk" className="h-full w-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#161210] via-espresso/35 to-espresso/25" />
      <div className="absolute inset-0 bg-gradient-to-r from-espresso/55 via-transparent to-transparent" />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-24 pt-40 md:px-10 md:pb-28">
        <motion.p
          data-testid="hero-eyebrow"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 1 }}
          className="mb-6 font-mono text-[11px] uppercase tracking-[0.35em] text-gold"
        >
          Custom Homes · Renovations · Remodeling
        </motion.p>

        <h1 className="font-serif text-6xl font-light leading-[1.02] text-cream sm:text-7xl lg:text-8xl">
          {LINES.map((line, i) => (
            <span key={i} className="hero-title-mask">
              <motion.span
                data-testid={`hero-title-line-${i + 1}`}
                className={`block ${line.italic ? 'italic text-gold' : ''}`}
                initial={{ y: '112%' }}
                animate={{ y: 0 }}
                transition={{ delay: 0.35 + i * 0.16, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          data-testid="hero-description"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-7 max-w-xl text-base font-light leading-relaxed text-cream/80 md:text-lg"
        >
          Invicto Homes designs and builds bespoke custom residences and carries out full-scale
          renovations and remodeling — every project finished to a craftsman's standard, made to
          outlive trends.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <button
            data-testid="hero-explore-cta"
            onClick={() => scrollToId('showcase')}
            className="group flex items-center gap-2 bg-gold px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.25em] text-espresso transition-all duration-300 hover:bg-goldhover"
          >
            Explore Our Work
            <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <button
            data-testid="hero-consult-cta"
            onClick={() => scrollToId('contact')}
            className="flex items-center gap-2 border border-cream/40 bg-cream/5 px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.25em] text-cream backdrop-blur-sm transition-all duration-300 hover:border-gold hover:text-gold"
          >
            Start Your Build
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        data-testid="hero-scroll-indicator"
        style={{ opacity: fade }}
        className="absolute bottom-8 right-8 z-10 hidden flex-col items-center gap-3 md:flex"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-cream/70 [writing-mode:vertical-rl]">Scroll</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.2, ease: 'easeInOut' }}>
          <ArrowDown size={14} className="text-gold" />
        </motion.span>
      </motion.div>
    </section>
  );
};
